import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  HiOutlineBell, HiOutlineChatAlt2, HiOutlineTrash, HiOutlineCheck,
  HiOutlineInboxIn, HiOutlineUserAdd, HiOutlineSpeakerphone, HiOutlineMail,
} from 'react-icons/hi'
import leadService, { LEAD_STATUS_LABELS } from '../../services/leadService'
import { useAuthStore } from '../../store/authStore'

const fmtRel = (s) => {
  if (!s) return ''
  const diff = (Date.now() - new Date(s).getTime()) / 1000
  if (diff < 60) return 'vừa xong'
  if (diff < 3600) return `${Math.floor(diff / 60)} phút`
  if (diff < 86400) return `${Math.floor(diff / 3600)} giờ`
  return `${Math.floor(diff / 86400)} ngày`
}

// Icon + màu theo loại thông báo
const TYPE_META = {
  new_lead: { icon: HiOutlineInboxIn, cls: 'bg-red-100 text-red-600', verb: 'cần phân luồng' },
  assigned: { icon: HiOutlineUserAdd, cls: 'bg-emerald-100 text-emerald-600', verb: 'được giao cho bạn' },
  campaign: { icon: HiOutlineSpeakerphone, cls: 'bg-violet-100 text-violet-600', verb: 'từ chiến dịch của bạn' },
  comment: { icon: HiOutlineChatAlt2, cls: 'bg-primary-100 text-primary-600', verb: 'bình luận' },
}

/**
 * Chuông thông báo — theo vai trò:
 * - Trưởng nhóm: lead mới cần phân luồng (new_lead) + lead chưa phân công.
 * - Nhân viên: lead được giao (assigned) / từ chiến dịch (campaign) + bình luận. Có bật/tắt nhận email.
 */
export default function NotificationBell() {
  const nav = useNavigate()
  const { user, setUser } = useAuthStore()
  const isMember = user?.role === 'member'
  const [data, setData] = useState(null)
  const [open, setOpen] = useState(false)
  const [emailOn, setEmailOn] = useState(user?.email_notify !== false)
  const ref = useRef(null)

  const load = () => leadService.notifications()
    .then((r) => { if (r.status === 'success') setData(r.data) })
    .catch(() => {})

  useEffect(() => {
    load()
    const t = setInterval(load, 60000)
    const onVis = () => document.visibilityState === 'visible' && load()
    document.addEventListener('visibilitychange', onVis)
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis) }
  }, [])

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const badge = data?.badge || 0
  const inbox = data?.inbox || []
  const unread = data?.unread || 0
  const go = (qs) => { setOpen(false); nav(`/admin/leads${qs}`) }

  const openItem = (n) => {
    if (!n.is_read) leadService.markNotifRead(n.id).then(load).catch(() => {})
    if (n.lead_id) go(`?open=${n.lead_id}`); else setOpen(false)
  }
  const markAllRead = () => leadService.markNotifRead().then(load).catch(() => {})
  const clearAll = () => leadService.clearNotifs().then(load).catch(() => {})
  const toggleEmail = () => {
    const next = !emailOn
    setEmailOn(next)
    leadService.updateNotifSettings({ email_notify: next })
      .then(() => setUser?.({ ...user, email_notify: next }))
      .catch(() => setEmailOn(!next))
  }

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} className="relative p-2 text-secondary-500 hover:text-secondary-800" title="Thông báo">
        <HiOutlineBell className="w-6 h-6" />
        {badge > 0 && (
          <span className="absolute top-0 right-0 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-2xs font-bold flex items-center justify-center">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[min(92vw,20rem)] bg-white rounded-xl shadow-xl border border-secondary-100 z-50 overflow-hidden">
          <div className="px-4 py-3 border-b border-secondary-100 flex items-center justify-between">
            <span className="font-medium text-secondary-800">Thông báo{unread > 0 && <span className="ml-1 text-2xs text-red-500">({unread} mới)</span>}</span>
            {(unread > 0 || inbox.length > 0) && (
              <div className="flex items-center gap-2 text-2xs">
                {unread > 0 && <button onClick={markAllRead} className="inline-flex items-center gap-0.5 text-primary-600 hover:text-primary-700" title="Đánh dấu tất cả đã đọc"><HiOutlineCheck className="w-3.5 h-3.5" /> Đã đọc</button>}
                {inbox.length > 0 && <button onClick={clearAll} className="inline-flex items-center gap-0.5 text-secondary-400 hover:text-red-600" title="Dọn hết thông báo"><HiOutlineTrash className="w-3.5 h-3.5" /> Dọn</button>}
              </div>
            )}
          </div>

          {/* Chốt nhanh theo vai trò */}
          <div className="p-2 space-y-1">
            {data?.is_manager ? (
              <button onClick={() => go('?assigned_to=unassigned')} className="w-full text-left px-3 py-2 rounded-lg hover:bg-secondary-50 flex justify-between items-center text-sm">
                <span>🕓 Lead chưa phân luồng</span><span className="font-bold text-red-600">{data?.unassigned || 0}</span>
              </button>
            ) : (
              <button onClick={() => go('')} className="w-full text-left px-3 py-2 rounded-lg hover:bg-secondary-50 flex justify-between items-center text-sm">
                <span>⚡ Hồ sơ cần xử lý</span><span className="font-bold text-primary-600">{data?.need_action || 0}</span>
              </button>
            )}
            <div className="w-full text-left px-3 py-2 rounded-lg flex justify-between items-center text-sm text-secondary-600">
              <span>⏰ Quá hẹn gọi lại</span><span className="font-bold text-amber-600">{data?.overdue_callback || 0}</span>
            </div>
          </div>

          {/* Danh sách thông báo (đã lưu, có đã đọc) */}
          {inbox.length > 0 && (
            <div className="border-t border-secondary-100 max-h-72 overflow-y-auto">
              {inbox.map((n) => {
                const m = TYPE_META[n.type] || TYPE_META.comment
                const Icon = m.icon
                return (
                  <button key={n.id} onClick={() => openItem(n)}
                    className={`w-full text-left px-4 py-2.5 hover:bg-secondary-50 border-b border-secondary-50 last:border-0 flex gap-2.5 ${n.is_read ? '' : 'bg-primary-50/50'}`}>
                    <div className={`mt-0.5 shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${m.cls}`}><Icon className="w-4 h-4" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-secondary-800 truncate">
                          {n.type === 'comment' ? <><b>{n.actor_name || 'Ai đó'}</b> {m.verb}</> : <span>{n.content || m.verb}</span>}
                        </span>
                        <span className="text-2xs text-secondary-400 shrink-0" title={n.created_at}>{fmtRel(n.created_at)}</span>
                      </div>
                      <div className="text-2xs text-secondary-500 truncate">📋 {n.lead_name || `#${n.lead_id}`}</div>
                      {n.type === 'comment' && n.content && <div className="text-xs text-secondary-600 truncate mt-0.5">“{n.content}”</div>}
                    </div>
                    {!n.is_read && <span className="mt-1 shrink-0 w-2 h-2 rounded-full bg-primary-500" />}
                  </button>
                )
              })}
            </div>
          )}

          {/* Cài đặt nhận email (chỉ nhân viên) */}
          {isMember && (
            <button onClick={toggleEmail} className="w-full border-t border-secondary-100 px-4 py-2.5 flex items-center justify-between text-sm hover:bg-secondary-50">
              <span className="inline-flex items-center gap-2 text-secondary-600"><HiOutlineMail className="w-4 h-4" /> Nhận email khi có thông báo</span>
              <span className={`relative inline-block w-9 h-5 rounded-full transition ${emailOn ? 'bg-primary-500' : 'bg-secondary-300'}`}>
                <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition ${emailOn ? 'left-4.5 translate-x-0' : 'left-0.5'}`} style={{ left: emailOn ? '1.125rem' : '0.125rem' }} />
              </span>
            </button>
          )}

          {inbox.length === 0 && <div className="px-4 py-3 text-2xs text-secondary-400 border-t border-secondary-100">Chưa có thông báo mới 🎉</div>}
        </div>
      )}
    </div>
  )
}
